import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints that legitimately return 401 for bad user input (wrong password,
// wrong SMS token) rather than an expired session — must NOT trigger the global
// redirect, or the error message never gets a chance to render before the page reloads.
const AUTH_401_IS_EXPECTED = ['/auth/login', '/auth/sms-token/verify', '/auth/change-password'];

// Handle responses
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const url = error.config?.url || '';
    const isExpected401 = AUTH_401_IS_EXPECTED.some((p) => url.includes(p));
    if (error.response?.status === 401 && !isExpected401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (email, password) => api.post('/auth/login', { email, password }),
  getInvitation: (token) => api.get(`/auth/invitations/${token}`),
  profile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  requestSmsToken: (roll_no, phone) => api.post('/auth/sms-token/request', { roll_no, phone }),
  verifySmsToken: (roll_no, token) => api.post('/auth/sms-token/verify', { roll_no, token }),
  forgotPassword: (email, method) => api.post('/auth/forgot-password', { email, method }),
  resetPassword: (email, token, new_password) => api.post('/auth/reset-password', { email, token, new_password }),
  uploadPicture: (file) => {
    const fd = new FormData();
    fd.append('picture', file);
    return api.post('/auth/me/picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const healthInsuranceAPI = {
  providers: () => api.get('/health-insurance/providers'),
  verify: (roll_number) => api.post('/health-insurance/verify', { roll_number }),
  submit: (data) => api.post('/health-insurance/submit', data),
  dashboard: () => api.get('/health-insurance/dashboard'),
  responses: (params) => api.get('/health-insurance/responses', { params }),
  adminProviders: () => api.get('/health-insurance/admin/providers'),
  createProvider: (provider_name) => api.post('/health-insurance/admin/providers', { provider_name }),
  updateProvider: (id, data) => api.put(`/health-insurance/admin/providers/${id}`, data),
};

// Members API
export const membersAPI = {
  list: (page = 1, limit = 20, search = '', status = '', admissionYear = '') =>
    api.get('/members', { params: { page, limit, search, status, admission_year: admissionYear } }),
  allIds: (search = '', status = '', admissionYear = '') =>
    api.get('/members', { params: { search, status, admission_year: admissionYear, ids_only: 1 } }),
  create: (data) => api.post('/members', data),
  sendInvitation: (data) => api.post('/members/invite', data),
  inviteById: (id, data) => api.post(`/members/${id}/invite`, data),
  me: () => api.get('/members/me'),
  get: (id) => api.get(`/members/${id}`),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  exempted: (search = '') => api.get('/members/exempted', { params: { search } }),
  uploadPicture: (file) => {
    const fd = new FormData();
    fd.append('picture', file);
    return api.post('/members/me/picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  search: (q) => api.get('/members/search', { params: { q } }),
  statuses: () => api.get('/members/statuses'),
  stats: () => api.get('/members/stats'),
  deceasedStats: () => api.get('/members/stats', { params: { type: 'deceased' } }),
  generatePublicUpdateLink: (id) => api.post(`/members/${id}/public-link`),
  outstanding: (id) => api.get(`/members/${id}/outstanding`),
  lifeEligibility: (id) => api.get(`/members/${id}/life-eligibility`),
};

// Pending Admissions API
export const pendingAdmissionAPI = {
  list: (page = 1, limit = 20, search = '', status = '', hasAccount = '') =>
    api.get('/pending-admissions', { params: { page, limit, search, status, has_account: hasAccount } }),
  import: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/pending-admissions/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  admit: (id, data) => api.post(`/pending-admissions/${id}/admit`, data),
  update: (id, data) => api.put(`/pending-admissions/${id}`, data),
  delete: (id) => api.delete(`/pending-admissions/${id}`),
  inviteAllCount: () => api.get('/pending-admissions/invite-all/count'),
  inviteAll: () => api.post('/pending-admissions/invite-all'),
  jobStatus: (jobId) => api.get(`/pending-admissions/jobs/${jobId}`),
};

// Beneficiaries API
export const beneficiariesAPI = {
  getByMember: (memberId) => api.get(`/beneficiaries/member/${memberId}`),
  create: (data) => api.post('/beneficiaries', data),
  get: (id) => api.get(`/beneficiaries/${id}`),
  update: (id, data) => api.put(`/beneficiaries/${id}`, data),
  delete: (id) => api.delete(`/beneficiaries/${id}`),
  validate: (memberId) => api.post(`/beneficiaries/validate/${memberId}`),
};

export const publicBeneficiariesAPI = {
  get: (token) => api.get(`/beneficiaries/public/${token}`),
  create: (token, data) => api.post('/beneficiaries/public', { token, ...data }),
  update: (id, token, data) => api.put(`/beneficiaries/public/${id}`, { token, ...data }),
  validate: (token) => api.post('/beneficiaries/public/validate', { token }),
};

// Payments API
export const paymentsAPI = {
  list: (page = 1, limit = 20, status = '', feeYear = '') =>
    api.get('/payments', { params: { page, limit, status, fee_year: feeYear } }),
  getByMember: (memberId) => api.get(`/payments/member/${memberId}`),
  create: (data) => api.post('/payments', data),
  get: (id) => api.get(`/payments/${id}`),
  generateQR: (id) => api.post(`/payments/qr/${id}`),
  markAsPaid: (id) => api.post(`/payments/mark-paid/${id}`),
  getGroup: (referenceNo) => api.get(`/payments/group/${referenceNo}`),
  getOutstanding: () => api.get('/payments/outstanding'),
  stats: (year) => api.get('/payments/stats', { params: { year } }),
};

// Invoice PDF downloads — fetch with auth header, trigger browser download.
// window.open() after async fetch is blocked by browsers as a popup,
// so we use an <a download> element instead.
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function fetchPdf(endpoint) {
  const token = localStorage.getItem('token');
  const r = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${r.status}`);
  }
  return r.blob();
}

export const invoiceAPI = {
  lookupMembersLife: (roll_nos) => api.post('/invoices/lookup-members-life', { roll_nos }),
  generateLife: (type, roll_nos) =>
    api.post('/invoices/generate-life', type === 'group' ? { type, roll_nos } : { type }),
  openPdf: async (id) => {
    const blob = await fetchPdf(`/api/invoices/${id}/pdf`);
    downloadBlob(blob, `AWTF-Invoice-${id}.pdf`);
  },
  openReceipt: async (id) => {
    const blob = await fetchPdf(`/api/invoices/${id}/receipt`);
    downloadBlob(blob, `AWTF-Receipt-${id}.pdf`);
  },
  generateArrears: () => api.post('/invoices/generate-arrears'),
  generateArrearsInstallment: (memberId) => api.post('/invoices/generate-arrears-installment', { member_id: memberId }),
  generateLifeTopup: () => api.post('/invoices/generate-life-topup'),
  lookupMembers: (rollNos) => api.post('/invoices/lookup-members', { roll_nos: rollNos }),
  generate: (type, rollNos, year) =>
    api.post('/invoices/generate', type === 'group'
      ? { type, roll_nos: rollNos }
      : { type, ...(year ? { year } : {}) }),
  openGroupPdf: async (ids, year, referenceNo) => {
    const token = localStorage.getItem('token');
    const r = await fetch('/api/invoices/group-pdf', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, reference_no: referenceNo }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
    const blob = await r.blob();
    downloadBlob(blob, `AWTF-Group-Invoice-${year}.pdf`);
  },
};

// CRDB Account Mappings API
export const crdbAPI = {
  list: () => api.get('/fee-config/crdb-accounts'),
  update: (id, data) => api.put(`/fee-config/crdb-accounts/${id}`, data),
};

// Payment Methods API
export const paymentMethodsAPI = {
  list:   ()           => api.get('/payment-methods'),
  all:    ()           => api.get('/payment-methods/all'),
  create: (data)       => api.post('/payment-methods', data),
  update: (id, data)   => api.put(`/payment-methods/${id}`, data),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post(`/payment-methods/${id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Fee Configuration API
export const feeConfigAPI = {
  getAll: () => api.get('/fee-config'),
  getByYear: (year) => api.get(`/fee-config/year/${year}`),
  saveAnnual: (data) => api.post('/fee-config/annual', data),
  saveBeneficiary: (data) => api.post('/fee-config/beneficiary', data),
  delete: (id) => api.delete(`/fee-config/${id}`),
  getLifeMembershipFees: () => api.get('/fee-config/life-membership'),
  saveLifeMembershipFees: (brackets, min_years) => api.post('/fee-config/life-membership', { brackets, min_years }),
};

// Claims API
export const claimsAPI = {
  list: (page = 1, limit = 20, status = '', search = '') =>
    api.get('/claims', { params: { page, limit, status, search } }),
  getByMember: (memberId) => api.get(`/claims/member/${memberId}`),
  create: (data) => api.post('/claims', data),
  get: (id) => api.get(`/claims/${id}`),
  setUnderReview: (id) => api.post(`/claims/under-review/${id}`),
  approve: (id) => api.post(`/claims/approve/${id}`),
  reject: (id, data) => api.post(`/claims/reject/${id}`, data),
  markAsPaid: (id) => api.post(`/claims/mark-paid/${id}`),
  uploadDocument: (id, formData) =>
    api.post(`/claims/upload/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addNote: (id, note) => api.post(`/claims/note/${id}`, { note }),
  selectBeneficiary: (claimId, cbId) => api.post(`/claims/select-beneficiary/${claimId}`, { cb_id: cbId }),
  sendToBoard: (id) => api.post(`/claims/send-to-board/${id}`),
  boardDecision: (id, decision, notes) => api.post(`/claims/board-decision/${id}`, { decision, notes }),
  getBoardPending: () => api.get('/claims/board-pending'),
  getBoardApprovals: (id) => api.get(`/claims/${id}/board-approvals`),
  stats: (year) => api.get('/claims/stats', { params: { year } }),
  payoutRegister: (page = 1, search = '') =>
    api.get('/claims/payout-register', { params: { page, search } }),
};

// Payment Transactions API
export const paymentTransAPI = {
  getByMember: (memberId) => api.get(`/payment-trans/member/${memberId}`),
};

// Payment Slips API (direct-deposit proof upload)
export const paymentSlipsAPI = {
  admissionReceipts: (page = 1, search = '', status = 'missing') =>
    api.get('/admission-receipts', { params: { page, search, status } }),
  upload: (formData) => api.post('/payment-slips', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getByMember: (memberId) => api.get(`/payment-slips/member/${memberId}`),
  list: (feeYear = '', page = 1, limit = 25, search = '', status = '') =>
    api.get('/payment-slips', { params: { fee_year: feeYear, page, limit, search, status } }),
  reject: (id, reason) => api.post(`/payment-slips/${id}/reject`, { reason }),
};

// Ad-hoc bulk SMS/email to a selected group of members
export const notificationsAPI = {
  send: (memberIds, channel, message, subject) =>
    api.post('/notifications/send', { member_ids: memberIds, channel, message, subject }),
  queue: (memberIds, channel, message, subject) =>
    api.post('/notifications/queue', { member_ids: memberIds, channel, message, subject }),
  jobStatus: (jobId) => api.get(`/notifications/jobs/${jobId}`),
};

// Staff / Users API (admin only)
export const usersAPI = {
  list: (role = '') => api.get('/users', { params: { role } }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

// System Settings API (admin only)
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getDepositSlipStatus: () => api.get('/settings/deposit-slip-status'),
};

// Error Logs API (admin only)
export const errorLogAPI = {
  list: (page = 1, limit = 25, level = '', search = '') =>
    api.get('/error-logs', { params: { page, limit, level, search } }),
  clear: () => api.delete('/error-logs/clear'),
};

// Permissions API (admin only)
export const permissionsAPI = {
  catalog: () => api.get('/permissions'),
  getForUser: (userId) => api.get(`/permissions/user/${userId}`),
  setForUser: (userId, permissions) => api.put(`/permissions/user/${userId}`, { permissions }),
};

// Reports API (admin/finance)
export const reportsAPI = {
  paymentsByYear:  () => api.get('/reports/payments-by-year'),
  memberOutstanding: (page = 1, limit = 25, search = '') =>
    api.get('/reports/member-outstanding', { params: { page, limit, search } }),
  memberStatusBreakdown: () => api.get('/reports/member-status-breakdown'),
  ageAnalysis: () => api.get('/reports/age-analysis'),
  beneficiaryAnalysis: () => api.get('/reports/beneficiary-analysis'),
  membersExport: () => api.get('/reports/members-export'),
};

// Zones & Chapters API
export const zonesAPI = {
  list: () => api.get('/zones'),
  create: (data) => api.post('/zones', data),
  update: (id, data) => api.put(`/zones/${id}`, data),
  delete: (id) => api.delete(`/zones/${id}`),
  createChapter: (zoneId, data) => api.post(`/zones/${zoneId}/chapters`, data),
  updateChapter: (id, data) => api.put(`/chapters/${id}`, data),
  deleteChapter: (id) => api.delete(`/chapters/${id}`),
};

// Audit Trail API (admin only)
export const auditLogAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  meta: () => api.get('/audit-logs/meta'),
};

// VPN Status API (admin only)
export const vpnStatusAPI = {
  get: () => api.get('/vpn-status'),
  reconnect: (connectionName) => api.post('/vpn-status/reconnect', { connection_name: connectionName }),
  createConnection: (data) => api.post('/vpn-status/connections', data),
  deleteConnection: (connectionName) => api.delete(`/vpn-status/connections/${connectionName}`),
};

// CRDB Requests log API (admin only)
export const crdbRequestsAPI = {
  list: (params) => api.get('/crdb-requests', { params }),
};

// Bank Reconciliation API (admin/finance)
export const bankReconciliationAPI = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('statement', file);
    return api.post('/bank-reconciliation/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listBatches: () => api.get('/bank-reconciliation/batches'),
  getBatch: (batchId, status) => api.get(`/bank-reconciliation/batches/${batchId}`, { params: status ? { status } : {} }),
  markPaid: (itemId) => api.post(`/bank-reconciliation/items/${itemId}/mark-paid`),
  markPaidAll: (batchId) => api.post(`/bank-reconciliation/batches/${batchId}/mark-paid-all`),
  suggestMembers: (batchId) => api.post(`/bank-reconciliation/batches/${batchId}/suggest-members`),
  importSuggested: (batchId, minConfidence) => api.post(`/bank-reconciliation/batches/${batchId}/import-suggested`, { min_confidence: minConfidence }),
  importItem: (itemId, memberId) => api.post(`/bank-reconciliation/items/${itemId}/import`, { member_id: memberId }),
  acceptAmount: (itemId) => api.post(`/bank-reconciliation/items/${itemId}/accept-amount`),
  splitRemainder: (itemId) => api.post(`/bank-reconciliation/items/${itemId}/split-remainder`),
  skipItem: (itemId) => api.post(`/bank-reconciliation/items/${itemId}/skip`),
};

export default api;
